import camelCase from 'lodash/camelCase';
import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AkTabs from '@atlaskit/tabs';
import Badge from '@atlaskit/badge';
import { UFOEventSource, tabChange } from '@atlassian/temp-observability';
import './tabs.scss';
import { AnalyticsUiAction, AnalyticsUiSubject, analytics } from '@atlassian/temp-analytics';

export const DEFAULT_TAB_TESTID = 'testid-tabs';

interface TabPanelProps {
  pathname?: string;
  children: React.ReactNode;
  enabled?: boolean;
  label: string;
}

/**
 * Wrapper for Tab content
 */
export const TabPanel = ({ enabled = true, label }: TabPanelProps) => {
  return <div data-enabled={enabled}>{label}</div>;
};

type TabChildType = React.ReactElement<TabPanelProps> | boolean | null;

interface TabsProps {
  children: TabChildType | TabChildType[];
  testId?: string;
  className?: string;
  onTabSelect?: (tabLabel: string) => void;
  selectedTabKey?: string;
  totalCountPerTab?: Record<string, number>;
}

interface TabData {
  pathname?: string;
  label?: React.ReactNode;
  content?: React.ReactNode;
  testId?: string;
  [key: string]: unknown;
}

/**
 * Used to wrap AtlasKit tabs for location hashing
 *
 */
export const Tabs = ({ children, testId, className, onTabSelect, selectedTabKey, totalCountPerTab }: TabsProps) => {
  const history = useHistory();
  const location = useLocation();

  const [selectedTabHash, setSelectedTabHash] = React.useState('');

  React.useEffect(() => {
    // logic to select the current tab as active based on location hash
    // e.g. label, PartnerAccounts => partner-accounts
    // e.g. label, Partner Accounts => partner-accounts
    setSelectedTabHash(kebabCase(location?.hash ?? ''));
  }, [location]);

  const generateTabs = (): TabData[] => {
    const visibleTabs = React.Children.toArray(children).filter((element: any) => {
      const isValid = React.isValidElement(element);
      const isEnabled = element.props.enabled ?? true;

      return isValid && isEnabled;
    }) as React.ReactElement<TabPanelProps>[];

    const mappedVisibleTabs = visibleTabs.map((tab) => {
      const key = kebabCase(tab.props.pathname ?? tab.props.label);
      const getLabel = () => {
        return totalCountPerTab && totalCountPerTab?.[tab.props.label] !== null ? (
          <>
            {tab.props.label} {<Badge>{totalCountPerTab?.[tab.props.label]}</Badge>}
          </>
        ) : (
          tab.props.label
        );
      };
      return {
        label: getLabel(),
        content: (
          <div className="tab-content" key={key} data-testid={`tab-content-${kebabCase(tab.props.label)}`}>
            {tab.props.children}
          </div>
        ),
        key,
      };
    });
    return mappedVisibleTabs;
  };
  const tabs = generateTabs();
  // selectedTabKey is used for tab inside tabs
  const selectedTab = tabs?.find((tab) => tab.key === (selectedTabKey || selectedTabHash));

  return (
    <div className={classNames('tabs-container', className)}>
      <AkTabs
        testId={testId ?? DEFAULT_TAB_TESTID}
        // @ts-ignore
        //TODO: need to upgrade atlaskit/tabs to fix the type issue which has major breaking changes
        tabs={tabs}
        selected={selectedTab || tabs?.[0]}
        onSelect={(tab) => {
          /**
           * UFO experience for tab change experience
           */
          //@ts-ignore
          //TODO: need to upgrade atlaskit/tabs to fix the type issue which has major breaking changes
          tab = { ...tab, label: tab?.label?.props?.children[0] || tab?.label };
          const result = tab.label as string;
          let parent;
          if (location.pathname) {
            if (location.pathname?.length === 1) {
              parent = UFOEventSource.BASE_PAGE;
            } else {
              const parentName = location.pathname.substring(1);
              parent = parentName.substring(0, parentName.indexOf('/'));
            }
          }
          const tabName = parent + '-' + result.replace(/\s+/g, '-').toLowerCase() + '-' + 'tab';
          const tabChanged = tabChange(tabName);
          if (onTabSelect) {
            // this method is used for tab inside tabs
            const tabName = JSON.stringify(location.hash);
            const tabInfo = tabName.substring(2);
            const tabIn = tabInfo.substring(0, tabInfo.length - 1);

            const tabInside = parent + '-' + tabIn + '-' + result + 'tab';
            const tabInsideTab = tabChange(tabInside);
            tabInsideTab.start();
            onTabSelect(tab.label as string);
            tabInsideTab.success();
          } else {
            tabChanged.start();
            history.replace({
              ...location,
              hash: kebabCase(tab.key ?? tab.label),
            });
            tabChanged.success();
            analytics.sendUIEvent({
              action: AnalyticsUiAction.CHANGED,
              actionSubject: AnalyticsUiSubject.TAB,
              actionSubjectId: camelCase(result),
            });
          }
        }}
      />
    </div>
  );
};
